import { useState, useCallback, useEffect, useRef } from 'react'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import Flashbar from '@cloudscape-design/components/flashbar'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Input from '@cloudscape-design/components/input'
import Link from '@cloudscape-design/components/link'
import ProgressBar from '@cloudscape-design/components/progress-bar'
import SegmentedControl from '@cloudscape-design/components/segmented-control'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import Textarea from '@cloudscape-design/components/textarea'
import Wizard from '@cloudscape-design/components/wizard'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import {
  useGmailQuota,
  useCreateEmailCampaign,
  useUpdateEmailCampaign,
  useReviewEmailCampaign,
  useImproveCampaignEmail,
  useGenerateCampaignEmail,
  useSendCampaign,
} from '@/hooks/useEmailCampaigns'
import { useProviders, useEnrichProviderEmail, useUpdateProviderEmail, useAddProvidersToPipeline } from '@/hooks/useProviders'
import { useClient } from '@/hooks/useClients'
import { useAccount } from '@/hooks/useAccount'
import { useCampaignAssistStore } from '@/state/campaignAssistStore'
import type { EmailCampaign, Provider } from '@/lib/types'
import SenderEmailBadge from '@/components/common/SenderEmailBadge'
import ProviderSelectionTable from './ProviderSelectionTable'
import EnterMissingEmailsModal from './EnterMissingEmailsModal'
import AddManualProviderModal from './AddManualProviderModal'

interface CampaignWizardProps {
  clientId: string
  onDismiss: () => void
  onComplete: (campaign: EmailCampaign) => void
  initialCampaign?: EmailCampaign
}

const QUOTA_WARNING_THRESHOLD = 400

interface ReviewResult {
  overallSeverity: string
  legalIssues: Array<{ category: string; description: string; severity: string }>
  marketingIssues: Array<{ category: string; description: string; severity: string }>
  suggestions: string[]
}

export default function CampaignWizard({ clientId, onDismiss, onComplete, initialCampaign }: CampaignWizardProps) {
  const [activeStep, setActiveStep] = useState(initialCampaign ? 1 : 0)
  const [navCreating, setNavCreating] = useState(false)

  // Step 1 — on resume, restore providers from the saved campaign so validation + AI generate work
  const [selectedProviders, setSelectedProviders] = useState<Provider[]>(
    () => initialCampaign?.providers.map((p) => ({
      _id: p.providerId,
      client_id: clientId,
      provider_name: p.providerName,
      specialty: '',
      practice_name: '',
      city: '',
      state: '',
      npi: p.providerNpi,
      email: p.email,
      status: 'new' as const,
      prescribing_volume_annual_usd: null,
      dme_product_mix: [],
      cms_rank_by_volume: null,
      talking_points: [],
      engagement_signals: [],
      created_at: '',
      updated_at: '',
    })) ?? []
  )

  // Step 0 — provider view toggle
  const [providerView, setProviderView] = useState<'campaign' | 'all'>('campaign')

  // Step 2
  const [campaignName, setCampaignName] = useState(initialCampaign?.name ?? '')
  const [subject, setSubject] = useState(initialCampaign?.subject ?? '')
  const [body, setBody] = useState(initialCampaign?.body ?? '')
  const [showImproved, setShowImproved] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')

  // Step 1 validation flash
  const [selectFlash, setSelectFlash] = useState<FlashbarProps.MessageDefinition[]>([])

  // Email search state for providers missing emails
  const [emailSearch, setEmailSearch] = useState<{
    state: 'idle' | 'searching' | 'done'
    done: number
    total: number
    found: number
  }>({ state: 'idle', done: 0, total: 0, found: 0 })

  // Manual entry modal states
  const [showEnterEmails, setShowEnterEmails] = useState(false)
  const [showAddManual, setShowAddManual] = useState(false)

  // Gemini phase 2 alert
  const [showGeminiAlert, setShowGeminiAlert] = useState(false)

  // Step 2 validation flash
  const [composeFlash, setComposeFlash] = useState<FlashbarProps.MessageDefinition[]>([])

  // Step 3
  const [reviewResult, setReviewResult] = useState<ReviewResult | null>(null)
  const [reviewFlash, setReviewFlash] = useState<FlashbarProps.MessageDefinition[]>([])

  // Step 4
  const [sendFlash, setSendFlash] = useState<FlashbarProps.MessageDefinition[]>([])
  const [campaign, setCampaign] = useState<EmailCampaign | null>(initialCampaign ?? null)

  // Data hooks
  const { account } = useAccount()
  const { client } = useClient(clientId)
  const campaignGoal = client?.marketingProfile?.campaignGoal ?? ''
  const { quota } = useGmailQuota(clientId)
  const { createCampaign, loading: creating } = useCreateEmailCampaign()
  const { updateCampaign } = useUpdateEmailCampaign()
  const { reviewCampaign, loading: reviewing } = useReviewEmailCampaign()
  const { improve, isImproving } = useImproveCampaignEmail(clientId)
  const { generateEmail } = useGenerateCampaignEmail(clientId)
  const { enrichEmail } = useEnrichProviderEmail()
  const { updateProviderEmail } = useUpdateProviderEmail()
  const { addProviders } = useAddProvidersToPipeline()
  const { providers } = useProviders(clientId)

  // Derived provider lists for Step 0
  const campaignProviders = providers.filter(p => p.selectedForCampaign)
  const visibleProviders = providerView === 'campaign' ? campaignProviders : providers

  // Pre-select campaign providers on first load (only once, don't override user selections)
  const didPreselect = useRef(false)
  useEffect(() => {
    if (didPreselect.current) return
    if (campaignProviders.length === 0) return
    if (initialCampaign) return // resuming — selections already restored
    didPreselect.current = true
    setSelectedProviders(campaignProviders)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignProviders.length])

  // Campaign assist store
  const campaignAssist = useCampaignAssistStore()
  const assistStatus = campaignAssist.status
  const improvedSubject = campaignAssist.improvedSubject
  const improvedBody = campaignAssist.improvedBody
  const assistError = campaignAssist.errorMessage

  // Send hook
  const {
    send: sendCampaign,
    status: sendStatus,
    progress: sendProgress,
    providerStatuses,
    quotaWarning: sendQuotaWarning,
    errorMessage: sendError,
  } = useSendCampaign(clientId, campaign?.id ?? '')

  // Auto-accept generated content when done
  useEffect(() => {
    if (assistStatus === 'done' && improvedSubject && improvedBody) {
      setSubject(improvedSubject)
      setBody(improvedBody)
      setShowImproved(false)
      useCampaignAssistStore.getState().reset()
      // Ensure campaign exists after AI generation
      if (!campaign) {
        void ensureCampaignExists()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistStatus])

  // Bug 12: Call onComplete in useEffect, not during render
  useEffect(() => {
    if (sendStatus === 'done' && campaign) {
      onComplete(campaign)
    }
  }, [sendStatus, campaign, onComplete])

  // Debounce timer ref
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const debouncedSave = useCallback(
    (name: string, subj: string, bod: string) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(async () => {
        const currentCampaign = campaign
        if (!currentCampaign || (currentCampaign.status !== 'draft' && currentCampaign.status !== 'in_progress')) return
        setSaveStatus('saving')
        await updateCampaign(clientId, currentCampaign.id, { name, subject: subj, body: bod })
        setSaveStatus('saved')
        setTimeout(() => setSaveStatus('idle'), 2000)
      }, 2000)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign, clientId, updateCampaign],
  )

  const providersWithEmail = selectedProviders.filter((p) => !!p.email)
  const providersWithoutEmail = selectedProviders.filter((p) => !p.email)

  // On resume, selectedProviders is empty (user skipped step 0). Fall back to the
  // campaign's saved provider list so the send step shows the right count and table.
  const effectiveSendProviders =
    providersWithEmail.length > 0
      ? providersWithEmail.map((p) => ({ name: p.provider_name, npi: p.npi }))
      : (campaign?.providers ?? []).map((p) => ({ name: p.providerName, npi: p.providerNpi }))
  const effectiveProviderCount = effectiveSendProviders.length

  const composeRecipients =
    providersWithEmail.length > 0
      ? providersWithEmail.map((p) => ({ name: p.provider_name, email: p.email! }))
      : (campaign?.providers ?? []).map((p) => ({ name: p.providerName, email: p.email }))

  const buildCampaignInput = useCallback(
    () => ({
      name: campaignName,
      subject,
      body,
      providers: providersWithEmail.map((p) => ({
        providerId: p._id,
        providerNpi: p.npi,
        providerName: p.provider_name,
        email: p.email!,
      })),
    }),
    [campaignName, subject, body, providersWithEmail],
  )

  const handleSearchEmails = useCallback(async () => {
    const toSearch = selectedProviders.filter((p) => !p.email)
    if (toSearch.length === 0) return
    setShowGeminiAlert(false)
    setEmailSearch({ state: 'searching', done: 0, total: toSearch.length, found: 0 })
    let found = 0
    let updatedProviders: typeof selectedProviders = selectedProviders
    for (const provider of toSearch) {
      try {
        const result = await enrichEmail(clientId, provider._id)
        const enriched = result.data?.enrichProviderEmail
        if (enriched?.email) {
          found++
          updatedProviders = updatedProviders.map((p) =>
            p._id === provider._id
              ? { ...p, email: enriched.email, email_last_checked: enriched.email_last_checked }
              : p,
          )
          setSelectedProviders(updatedProviders)
        }
      } catch {
        // non-fatal: count as not found
      }
      setEmailSearch((prev) => ({ ...prev, done: prev.done + 1 }))
    }
    setEmailSearch((prev) => ({ ...prev, state: 'done', found }))
    const notFound = toSearch.length - found
    if (found === toSearch.length) {
      setSelectFlash([])
    } else if (found > 0) {
      setSelectFlash([{
        type: 'info',
        content: `Found emails for ${found} of ${toSearch.length} provider${toSearch.length !== 1 ? 's' : ''}. ${notFound} provider${notFound !== 1 ? 's' : ''} will be skipped — would you like to continue with the ${found} that have emails?`,
        dismissible: true,
        onDismiss: () => setSelectFlash([]),
      }])
    } else {
      setSelectFlash([{
        type: 'warning',
        content: `No email addresses found for the ${toSearch.length} selected provider${toSearch.length !== 1 ? 's' : ''}. Select providers with known email addresses or try again.`,
        dismissible: true,
        onDismiss: () => setSelectFlash([]),
      }])
    }
    // Show Gemini phase 2 alert if applicable
    const stillMissingEmail = updatedProviders.some((p) => !p.email)
    if (stillMissingEmail && account?.modelProvider === 'google-genai') {
      setShowGeminiAlert(true)
    }
  }, [selectedProviders, enrichEmail, clientId, account?.modelProvider])

  const ensureCampaignExists = useCallback(
    async (): Promise<EmailCampaign | null> => {
      if (campaign) return campaign
      const res = await createCampaign(clientId, buildCampaignInput())
      const created = res.data?.createEmailCampaign ?? null
      if (created) setCampaign(created)
      return created
    },
    [campaign, createCampaign, clientId, buildCampaignInput],
  )

  const handleContinueWithPartial = useCallback(() => {
    setSelectFlash([])
    setEmailSearch({ state: 'idle', done: 0, total: 0, found: 0 })
    setActiveStep(1)
  }, [])

  const handleApplyEmails = useCallback((emails: Record<string, string>) => {
    setSelectedProviders((prev) =>
      prev.map((p) => (emails[p._id] ? { ...p, email: emails[p._id] } : p)),
    )
    setShowEnterEmails(false)
    setSelectFlash([])
    // Persist emails for non-manual providers in parallel (fire-and-forget)
    const persistUpdates = Object.entries(emails)
      .filter(([providerId, email]) => !providerId.startsWith('manual-') && !!email)
      .map(([providerId, email]) =>
        updateProviderEmail(clientId, providerId, email).catch((err: unknown) => {
          console.error('Failed to persist email for provider', providerId, err)
        }),
      )
    void Promise.all(persistUpdates)
  }, [clientId, updateProviderEmail])

  const handleGeminiPhase2 = useCallback(async () => {
    setShowGeminiAlert(false)
    const toSearch = selectedProviders.filter((p) => !p.email)
    if (toSearch.length === 0) return
    setEmailSearch({ state: 'searching', done: 0, total: toSearch.length, found: 0 })
    for (const provider of toSearch) {
      try {
        const result = await enrichEmail(clientId, provider._id, true)
        const enriched = result.data?.enrichProviderEmail
        if (enriched?.email) {
          setSelectedProviders((prev) =>
            prev.map((p) =>
              p._id === provider._id
                ? { ...p, email: enriched.email, email_last_checked: enriched.email_last_checked }
                : p,
            ),
          )
        }
      } catch {
        // non-fatal
      }
      setEmailSearch((prev) => ({ ...prev, done: prev.done + 1 }))
    }
    setEmailSearch((prev) => ({ ...prev, state: 'done' }))
  }, [selectedProviders, enrichEmail, clientId])

  const handleAddManualProvider = useCallback((provider: Provider) => {
    setSelectedProviders((prev) => [...prev, provider])
    setShowAddManual(false)
    void addProviders(clientId, [provider]).catch(() => {})
  }, [addProviders, clientId])

  const handleNavigate = useCallback(
    async (requestedStep: number) => {
      // Validate step 1: providers must be selected and all must have emails
      if (activeStep === 0 && requestedStep > 0) {
        if (providersWithEmail.length === 0) {
          setEmailSearch({ state: 'idle', done: 0, total: 0, found: 0 })
          setSelectFlash([{
            type: 'warning',
            content: `None of the ${selectedProviders.length} selected provider${selectedProviders.length !== 1 ? 's' : ''} have email addresses. Enter emails manually or search for emails to continue.`,
            dismissible: true,
            onDismiss: () => setSelectFlash([]),
          }])
          return
        }
        if (providersWithoutEmail.length > 0) {
          setEmailSearch({ state: 'idle', done: 0, total: 0, found: 0 })
          setSelectFlash([{
            type: 'warning',
            content: `${providersWithoutEmail.length} of ${selectedProviders.length} selected provider${selectedProviders.length !== 1 ? 's' : ''} are missing email addresses. Enter emails manually, search for emails, or continue with only the ${providersWithEmail.length} that have emails.`,
            dismissible: true,
            onDismiss: () => setSelectFlash([]),
          }])
          return
        }
      }
      setSelectFlash([])

      // Validate step 2: all compose fields must be filled
      if (activeStep === 1 && requestedStep > 1) {
        const missing: string[] = []
        if (!campaignName.trim()) missing.push('Campaign name')
        if (!subject.trim()) missing.push('Subject')
        if (!body.trim()) missing.push('Body')
        if (missing.length > 0) {
          setComposeFlash([{
            type: 'error',
            content: `Please fill in: ${missing.join(', ')}.`,
            dismissible: true,
            onDismiss: () => setComposeFlash([]),
          }])
          return
        }
      }
      setComposeFlash([])

      // Ensure campaign exists before entering step 4 (Confirm & Send)
      if (requestedStep === 3 && !campaign) {
        setNavCreating(true)
        try {
          const created = await ensureCampaignExists()
          if (!created) {
            setReviewFlash([{
              type: 'error',
              content: 'Failed to save campaign. Please try again.',
              dismissible: true,
              onDismiss: () => setReviewFlash([]),
            }])
            setNavCreating(false)
            return
          }
        } catch (err) {
          setReviewFlash([{
            type: 'error',
            content: `Failed to save campaign: ${err instanceof Error ? err.message : 'Unknown error'}`,
            dismissible: true,
            onDismiss: () => setReviewFlash([]),
          }])
          setNavCreating(false)
          return
        }
        setNavCreating(false)
      }
      setActiveStep(requestedStep)
    },
    [activeStep, providersWithEmail.length, providersWithoutEmail.length, selectedProviders.length, campaignName, subject, body, campaign, ensureCampaignExists, handleSearchEmails],
  )

  const handleGenerateEmail = useCallback(async () => {
    const specialties = selectedProviders.map((p) => p.specialty).filter(Boolean)
    const targetAudience =
      specialties.length > 0
        ? specialties.sort(
            (a, b) =>
              specialties.filter((s) => s === b).length -
              specialties.filter((s) => s === a).length,
          )[0]
        : 'healthcare providers'
    await generateEmail({ targetAudience })
  }, [generateEmail, selectedProviders])

  const handleAiImprove = useCallback(async () => {
    setShowImproved(false)
    await improve(subject, body)
    setShowImproved(true)
  }, [improve, subject, body])

  const handleAcceptImproved = () => {
    if (improvedSubject) setSubject(improvedSubject)
    if (improvedBody) setBody(improvedBody)
    setShowImproved(false)
    useCampaignAssistStore.getState().reset()
  }

  const handleRunReview = async () => {
    setReviewFlash([])
    try {
      const activeCampaign = await ensureCampaignExists()
      if (!activeCampaign) throw new Error('Could not create campaign')
      const rev = await reviewCampaign(clientId, activeCampaign.id)
      if (rev.data?.reviewBulkEmail) setReviewResult(rev.data.reviewBulkEmail)
    } catch (err) {
      setReviewFlash([{
        type: 'error',
        content: `Review failed: ${err instanceof Error ? err.message : 'Unknown error'}. You may still proceed.`,
        dismissible: true,
        onDismiss: () => setReviewFlash([]),
      }])
    }
  }

  const handleSend = async () => {
    setSendFlash([])
    try {
      await sendCampaign(effectiveProviderCount)
    } catch (err) {
      setSendFlash([{
        type: 'error',
        content: `Send failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        dismissible: true,
        onDismiss: () => setSendFlash([]),
      }])
    }
  }

  const isSending = sendStatus === 'sending'

  return (
    <Wizard
      i18nStrings={{
        stepNumberLabel: (n) => `Step ${n}`,
        collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
        navigationAriaLabel: 'Steps',
        cancelButton: 'Cancel',
        previousButton: 'Previous',
        nextButton: 'Next',
        submitButton: isSending ? 'Sending…' : 'Send Campaign',
        optional: 'optional',
      }}
      onCancel={onDismiss}
      onNavigate={({ detail }) => { void handleNavigate(detail.requestedStepIndex) }}
      onSubmit={() => { void handleSend() }}
      activeStepIndex={activeStep}
      isLoadingNextStep={navCreating || isSending || emailSearch.state === 'searching'}
      steps={[
        {
          title: 'Select Providers',
          description: 'Choose pipeline providers to receive this campaign email.',
          content: (
            <SpaceBetween size="m">
              {selectFlash.length > 0 && <Flashbar items={selectFlash} />}
              {emailSearch.state === 'searching' && (
                <ProgressBar
                  value={emailSearch.total > 0 ? Math.round((emailSearch.done / emailSearch.total) * 100) : 0}
                  label={`Searching for email addresses… ${emailSearch.done} / ${emailSearch.total}`}
                  status="in-progress"
                />
              )}
              {selectFlash.length > 0 && (
                <SpaceBetween direction="horizontal" size="xs">
                  {providersWithoutEmail.length > 0 && (
                    <Button
                      iconName="edit"
                      onClick={() => setShowEnterEmails(true)}
                    >
                      Enter missing emails
                    </Button>
                  )}
                  {providersWithoutEmail.length > 0 && (
                    <Button
                      loading={emailSearch.state === 'searching'}
                      onClick={() => { void handleSearchEmails() }}
                    >
                      {emailSearch.state === 'done' ? 'Search Again' : 'Search for Emails'}
                    </Button>
                  )}
                  {providersWithEmail.length > 0 && (
                    <Button
                      variant="primary"
                      disabled={emailSearch.state === 'searching'}
                      onClick={handleContinueWithPartial}
                    >
                      Continue with {providersWithEmail.length} provider{providersWithEmail.length !== 1 ? 's' : ''}
                    </Button>
                  )}
                </SpaceBetween>
              )}
              {providersWithEmail.length > QUOTA_WARNING_THRESHOLD && (
                <Alert type="warning">
                  {providersWithEmail.length} providers selected. Gmail limits personal accounts to 500
                  emails/day and Workspace to 2,000/day. Consider splitting into multiple campaigns.
                </Alert>
              )}
              {showGeminiAlert && (
                <Alert
                  type="info"
                  header="Some emails couldn't be found"
                  dismissible
                  onDismiss={() => setShowGeminiAlert(false)}
                  action={
                    <Button onClick={() => { void handleGeminiPhase2() }}>Try with Gemini</Button>
                  }
                >
                  Gemini can search the web to find emails for the remaining providers. This uses your configured Gemini API key.
                </Alert>
              )}
              <SegmentedControl
                selectedId={providerView}
                onChange={({ detail }) => setProviderView(detail.selectedId as 'campaign' | 'all')}
                options={[
                  { id: 'campaign', text: `Campaign List (${campaignProviders.length})` },
                  { id: 'all', text: 'All Providers' },
                ]}
              />
              {providerView === 'campaign' && campaignProviders.length === 0 && (
                <Alert type="info">
                  No providers have been added to the campaign list. Go to the Directory tab, select
                  providers, and click &apos;Add to Campaign&apos;.
                </Alert>
              )}
              <ProviderSelectionTable
                clientId={clientId}
                providers={visibleProviders}
                selectedProviders={selectedProviders}
                onSelectionChange={setSelectedProviders}
              />
              <SpaceBetween direction="horizontal" size="xs">
                {providersWithoutEmail.length > 0 && (
                  <Button
                    iconName="edit"
                    onClick={() => setShowEnterEmails(true)}
                  >
                    Enter missing emails ({providersWithoutEmail.length})
                  </Button>
                )}
                <Button iconName="add-plus" onClick={() => setShowAddManual(true)}>
                  Add provider manually
                </Button>
              </SpaceBetween>
              {providersWithEmail.length > 0 && (
                <Box variant="small" color="text-status-inactive">
                  {providersWithEmail.length} provider{providersWithEmail.length !== 1 ? 's' : ''} will
                  receive this email.
                  {providersWithoutEmail.length > 0 &&
                    ` ${providersWithoutEmail.length} skipped (no email).`}
                  {` Estimated send time: ~${providersWithEmail.length}s`}
                </Box>
              )}
              <EnterMissingEmailsModal
                key={showEnterEmails ? 'open' : 'closed'}
                providers={providersWithoutEmail}
                visible={showEnterEmails}
                onDismiss={() => setShowEnterEmails(false)}
                onApply={handleApplyEmails}
              />
              <AddManualProviderModal
                clientId={clientId}
                visible={showAddManual}
                onDismiss={() => setShowAddManual(false)}
                onAdd={handleAddManualProvider}
              />
            </SpaceBetween>
          ),
        },
        {
          title: 'Compose Email',
          content: (
            <SpaceBetween size="l">
              {composeFlash.length > 0 && <Flashbar items={composeFlash} />}
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Header variant="h2">Compose Email</Header>
                {saveStatus === 'saving' && (
                  <StatusIndicator type="loading">Saving…</StatusIndicator>
                )}
                {saveStatus === 'saved' && (
                  <StatusIndicator type="success">Saved</StatusIndicator>
                )}
              </SpaceBetween>
              <ExpandableSection headerText={`Recipients (${composeRecipients.length})`}>
                <Table
                  variant="embedded"
                  columnDefinitions={[
                    { id: 'name', header: 'Provider', cell: (item) => item.name },
                    { id: 'email', header: 'Email', cell: (item) => item.email },
                  ]}
                  items={composeRecipients}
                  empty={<Box color="text-body-secondary">No recipients selected.</Box>}
                />
              </ExpandableSection>
              <Container
                header={<Header variant="h3">Generate Email with AI</Header>}
                footer={
                  assistStatus === 'improving' ? (
                    <StatusIndicator type="loading">Generating email draft…</StatusIndicator>
                  ) : null
                }
              >
                <SpaceBetween size="s">
                  <Box variant="p">Let AI write a campaign draft based on your selected providers.</Box>
                  {campaignGoal ? (
                    <Box variant="p">
                      <Box variant="span" fontWeight="bold">Campaign goal: </Box>
                      <Box variant="span">{campaignGoal}</Box>
                      <Box variant="span"> — </Box>
                      <Link href={`/clients/${clientId}/configure`} target="_blank">Edit in Configure</Link>
                    </Box>
                  ) : (
                    <Box variant="p" color="text-status-warning">
                      No campaign goal set. <Link href={`/clients/${clientId}/configure`} target="_blank">Set one in Configure</Link> to improve draft quality.
                    </Box>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => { void handleGenerateEmail() }}
                    loading={assistStatus === 'improving'}
                    disabled={assistStatus === 'improving'}
                  >
                    {assistStatus === 'done' || subject || body ? 'Regenerate Email' : 'Generate Email'}
                  </Button>
                </SpaceBetween>
              </Container>
              <FormField label="Campaign name">
                <Input
                  value={campaignName}
                  onChange={({ detail }) => {
                    setCampaignName(detail.value)
                    debouncedSave(detail.value, subject, body)
                  }}
                  placeholder="e.g. Q2 DME Outreach"
                />
              </FormField>
              <FormField label="Subject">
                <Input
                  value={subject}
                  onChange={({ detail }) => {
                    setSubject(detail.value)
                    debouncedSave(campaignName, detail.value, body)
                  }}
                  placeholder="Enter email subject…"
                />
              </FormField>
              <FormField label="Body">
                <Textarea
                  value={body}
                  onChange={({ detail }) => {
                    setBody(detail.value)
                    debouncedSave(campaignName, subject, detail.value)
                  }}
                  rows={10}
                  placeholder="Write your outreach email here…"
                />
              </FormField>
              <Box variant="small" color="text-status-inactive">
                CAN-SPAM reminder: bulk marketing emails must include an unsubscribe mechanism
                (e.g. "Reply STOP to unsubscribe").
              </Box>
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={!subject.trim() || !body.trim() || isImproving}
                  loading={isImproving}
                  onClick={() => { void handleAiImprove() }}
                >
                  {isImproving ? 'Improving…' : 'AI Improve'}
                </Button>
              </SpaceBetween>
              {assistError && assistStatus === 'error' && (
                <Alert
                  type="error"
                  action={
                    <Button onClick={() => { void handleAiImprove() }}>Try Again</Button>
                  }
                >
                  AI improvement failed: {assistError}
                </Alert>
              )}
              {showImproved && improvedSubject && improvedBody && (
                <SpaceBetween size="m">
                  <ColumnLayout columns={2} borders="vertical">
                    <SpaceBetween size="s">
                      <Box variant="awsui-key-label">Original</Box>
                      <Box variant="awsui-key-label">Subject</Box>
                      <Box>{subject}</Box>
                      <Box variant="awsui-key-label">Body</Box>
                      <Box>{body}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="s">
                      <Box variant="awsui-key-label">Improved</Box>
                      <Box variant="awsui-key-label">Subject</Box>
                      <Box>{improvedSubject}</Box>
                      <Box variant="awsui-key-label">Body</Box>
                      <Box>{improvedBody}</Box>
                    </SpaceBetween>
                  </ColumnLayout>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="primary" onClick={handleAcceptImproved}>
                      Accept Improved Version
                    </Button>
                    <Button
                      onClick={() => {
                        setShowImproved(false)
                        useCampaignAssistStore.getState().reset()
                      }}
                    >
                      Keep Original
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              )}
            </SpaceBetween>
          ),
        },
        {
          title: 'Review',
          content: (
            <SpaceBetween size="l">
              {reviewFlash.length > 0 && <Flashbar items={reviewFlash} />}
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  loading={reviewing || creating}
                  onClick={() => { void handleRunReview() }}
                >
                  {reviewing || creating ? 'Running Review…' : 'Run AI Review'}
                </Button>
              </SpaceBetween>
              {reviewResult && (
                <SpaceBetween size="m">
                  <StatusIndicator
                    type={
                      reviewResult.overallSeverity === 'HIGH'
                        ? 'error'
                        : reviewResult.overallSeverity === 'MEDIUM'
                          ? 'warning'
                          : 'success'
                    }
                  >
                    Overall: {reviewResult.overallSeverity}
                  </StatusIndicator>
                  {reviewResult.overallSeverity === 'HIGH' && (
                    <Alert type="error">
                      HIGH severity issues detected. Resolve them before proceeding to send.
                    </Alert>
                  )}
                  <ExpandableSection
                    headerText={`Legal Issues (${reviewResult.legalIssues.length})`}
                  >
                    <SpaceBetween size="xs">
                      {reviewResult.legalIssues.length === 0 ? (
                        <Box color="text-status-inactive">No legal issues found.</Box>
                      ) : (
                        reviewResult.legalIssues.map((issue, i) => (
                          <SpaceBetween key={i} direction="horizontal" size="xs">
                            <StatusIndicator
                              type={
                                issue.severity === 'HIGH'
                                  ? 'error'
                                  : issue.severity === 'MEDIUM'
                                    ? 'warning'
                                    : 'info'
                              }
                            >
                              {issue.category}
                            </StatusIndicator>
                            <Box variant="small">{issue.description}</Box>
                          </SpaceBetween>
                        ))
                      )}
                    </SpaceBetween>
                  </ExpandableSection>
                  <ExpandableSection
                    headerText={`Marketing Issues (${reviewResult.marketingIssues.length})`}
                  >
                    <SpaceBetween size="xs">
                      {reviewResult.marketingIssues.length === 0 ? (
                        <Box color="text-status-inactive">No marketing issues found.</Box>
                      ) : (
                        reviewResult.marketingIssues.map((issue, i) => (
                          <SpaceBetween key={i} direction="horizontal" size="xs">
                            <StatusIndicator
                              type={
                                issue.severity === 'HIGH'
                                  ? 'error'
                                  : issue.severity === 'MEDIUM'
                                    ? 'warning'
                                    : 'info'
                              }
                            >
                              {issue.category}
                            </StatusIndicator>
                            <Box variant="small">{issue.description}</Box>
                          </SpaceBetween>
                        ))
                      )}
                    </SpaceBetween>
                  </ExpandableSection>
                  {reviewResult.suggestions.length > 0 && (
                    <ExpandableSection
                      headerText={`Suggestions (${reviewResult.suggestions.length})`}
                    >
                      <SpaceBetween size="xs">
                        {reviewResult.suggestions.map((s, i) => (
                          <Box key={i} variant="p">• {s}</Box>
                        ))}
                      </SpaceBetween>
                    </ExpandableSection>
                  )}
                </SpaceBetween>
              )}
              {quota && (
                <SpaceBetween size="s">
                  <Box variant="awsui-key-label">Gmail Quota</Box>
                  <ColumnLayout columns={3} borders="horizontal">
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Sent Today</Box>
                      <Box>{quota.sentCount}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Daily Limit</Box>
                      <Box>{quota.dailyLimit}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Can Send</Box>
                      <StatusIndicator type={quota.canSend ? 'success' : 'error'}>
                        {quota.canSend ? 'Yes' : 'No — limit reached'}
                      </StatusIndicator>
                    </SpaceBetween>
                  </ColumnLayout>
                  {quota.warningMessage && (
                    <Alert type="warning">{quota.warningMessage}</Alert>
                  )}
                </SpaceBetween>
              )}
            </SpaceBetween>
          ),
        },
        {
          title: 'Confirm & Send',
          content: (
            <SpaceBetween size="l">
              {sendFlash.length > 0 && <Flashbar items={sendFlash} />}
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Box variant="awsui-key-label">Sending from</Box>
                <SenderEmailBadge
                  platforms={account?.platforms ?? []}
                  defaultEmailProvider={account?.defaultEmailProvider}
                />
              </SpaceBetween>
              <Alert type="info">
                Gmail limits: Personal — 500 emails/day · Workspace — 2,000 emails/day · Emails are sent 1
                per second to respect API rate limits.
              </Alert>
              <ColumnLayout columns={3} borders="horizontal">
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label">Campaign</Box>
                  <Box>{campaignName}</Box>
                </SpaceBetween>
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label">Providers</Box>
                  <Box>{effectiveProviderCount} recipients</Box>
                </SpaceBetween>
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label">Estimated Time</Box>
                  <Box>~{effectiveProviderCount}s</Box>
                </SpaceBetween>
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label">Subject</Box>
                  <Box>{subject.length > 70 ? `${subject.slice(0, 70)}…` : subject}</Box>
                </SpaceBetween>
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label">Skipped</Box>
                  <Box>
                    {providersWithoutEmail.length > 0
                      ? `${providersWithoutEmail.length} (no email)`
                      : 'None'}
                  </Box>
                </SpaceBetween>
              </ColumnLayout>
              {isSending && (
                <SpaceBetween size="m">
                  <ProgressBar
                    value={
                      sendProgress.total > 0
                        ? Math.round(
                            ((sendProgress.sent + sendProgress.failed) / sendProgress.total) * 100,
                          )
                        : 0
                    }
                    label={`Sending… ${sendProgress.sent + sendProgress.failed} / ${sendProgress.total}`}
                    status="in-progress"
                  />
                  {sendQuotaWarning && <Alert type="warning">{sendQuotaWarning}</Alert>}
                  <Table
                    items={effectiveSendProviders.map((p) => ({
                      name: p.name,
                      npi: p.npi,
                      pStatus: providerStatuses[p.npi] ?? 'pending',
                    }))}
                    columnDefinitions={[
                      {
                        id: 'name',
                        header: 'Provider',
                        cell: (item) => item.name,
                      },
                      {
                        id: 'status',
                        header: 'Status',
                        cell: (item) => {
                          if (item.pStatus === 'sent')
                            return <StatusIndicator type="success">Sent</StatusIndicator>
                          if (item.pStatus === 'failed')
                            return <StatusIndicator type="error">Failed</StatusIndicator>
                          return <StatusIndicator type="pending">Pending</StatusIndicator>
                        },
                      },
                    ]}
                    variant="embedded"
                  />
                </SpaceBetween>
              )}
              {sendStatus === 'error' && sendError && (
                <Alert type="error">{sendError}</Alert>
              )}
            </SpaceBetween>
          ),
        },
      ]}
    />
  )
}
